/**
 * RWA Royale — client entry point.
 *
 * The loop, in order, every frame:
 *
 *   1. FIXED STEP at TICK_HZ: sample one InputFrame per tick, run it through the
 *      predictor immediately, record it for reconciliation, queue it for send.
 *   2. RENDER at display rate: interpolate the predicted body across the partial
 *      tick, sample remote players 100ms in the past from the snapshot buffer,
 *      update VFX and HUD.
 *
 * The two rates are separate on purpose. The simulation is a fixed 30Hz on both
 * sides — a variable-dt client would produce a different position from the same
 * inputs and reconcile against the server forever. Rendering is uncapped.
 *
 * What this file never does: write a position, a health value, a cooldown, a
 * kill or a winner into anything the server reads. It sends buttons and it draws
 * what it is told.
 */

import * as THREE from 'three/webgpu';
import { TICK_MS, CHARACTERS, abilityAnchor } from '@chaos/protocol';
import { PLAYER_HALF_HEIGHT, DASH_DURATION_MS } from '@chaos/sim';
import { createScene } from './SceneSetup.js';
import { renderFrame } from './PostProcessing.js';
import { CameraController, setCameraGroundLevel } from './CameraController.js';
import { initAimReticle, updateAimReticle } from './combat/AimReticle.js';
import { ArenaRenderer } from './ArenaRenderer.js';
import { ZoneRenderer } from './ZoneRenderer.js';
import { getMapDef, getMapMeta, known as mapKnown } from './maps/index.js';
import { createCharacterModel, updateDashTrail, setCharacterShimmer } from './characters/CharacterModels.js';
import { updateSquashStretch, updateLeaderCrown, updateCastPose, applyCastAnticipation } from './PlayerVisuals.js';
import { initRemotePlayers, applyPoses } from './rendering/RemotePlayers.js';
import { attachHealthBar, updateHealthBar } from './combat/HealthBars.js';
import { initAbilityVFX, updateAbilityVFX, playAbilityCast, playHitVFX, playChainArc, playEliminationVFX } from './combat/AbilityVFX.js';
// Namespaced so cross-workstream helpers can be called behind `?.` guards while
// other workstreams are still landing (flashHitTarget, setDeathGrade).
import * as abilityVFX from './combat/AbilityVFX.js';
import * as postFX from './PostProcessing.js';
import { initTelegraphs, setTelegraphFloor, showTelegraph, updateTelegraphs } from './combat/Telegraphs.js';
import { initHitFeedback, onDealtDamage, onTookDamage, onHealthChanged, onKillConfirmed, updateHitFeedback, isHitstopped, resetHitFeedback } from './combat/HitFeedback.js';
import { updateParticles, screenFlash, spawnParticles, triggerCameraShake } from './vfx/ScreenEffects.js';
import { prewarmShaders } from './vfx/ShaderPrewarm.js';
import { updateEnvironmentEffects } from './EnvironmentEffects.js';
import { updateConveyorScrolls } from './SurfaceShaders.js';
import { CombatHUD } from './ui/CombatHUD.js';
import { KillFeed } from './ui/KillFeed.js';
import { DeathCam } from './ui/DeathCam.js';
import { Announcements, zoneLabel } from './ui/Announcements.js';
import { setupKeyboardInput, consumeActions, readIntent, releaseAll, keys, getAimSlot } from './input/InputManager.js';
import { setStatusAura } from './combat/StatusAura.js';
import { setupMobileControls } from './input/MobileControls.js';
import { Connection } from './net/connection.js';
import { postMatchEnded } from './net/shellSession.js';
import { InputSender } from './net/inputSender.js';
import { Predictor } from './net/predict.js';
import { Reconciler } from './net/reconcile.js';
import { SnapshotBuffer } from './net/snapshotBuffer.js';
import { session, localPlayer, roster, remotePlayers, match, frame, cameraState } from './state.js';
import { shortAngleDist } from './math.js';
import { INPUT_FLUSH_MS, MAX_CATCHUP_TICKS, isDebug, isTouch, readPotWei } from './config.js';
import {
  playDashSound,
  playCastSound,
  playJumpSound,
  playLandSound,
  playFootstep,
  playSpawnSound,
  playDeathSound,
  playCountdownBeep,
  playWinFanfare,
  startTensionDrone,
  unlockAudio,
} from './audio/SoundManager.js';

const boot = {
  el: document.getElementById('boot-overlay'),
  status: document.getElementById('boot-status'),
};

function setBootStatus(text, isError = false) {
  boot.status.textContent = text;
  boot.status.classList.toggle('error', isError);
}

const _scratchA = new THREE.Vector3();
const _scratchB = new THREE.Vector3();

class Game {
  /**
   * @param {object} [connection] anything with the `Connection` surface. The gym
   *   (`src/gym/`) passes a LocalConnection that runs `@chaos/sim` in this tab
   *   instead of talking to a room; everything below is unaware of the swap.
   */
  constructor(connection = new Connection()) {
    this.connection = connection;
    this.sender = new InputSender(this.connection);
    this.predictor = null;
    this.reconciler = null;
    this.snapshots = new SnapshotBuffer();

    this.accumulatorMs = 0;
    this.lastFrameTime = 0;
    this.lastFlush = 0;
    /** Server tick we believe we are on. Corrected by every snapshot. */
    this.tick = 0;
    this.ready = false;
    this.dead = false;
    this._lastHealth = null;

    // Presentation edge-detection state. All of it reads the predictor or the
    // snapshot mirror — none of it feeds back into the sim.
    this._wasGrounded = true;
    this._peakFallVy = 0;
    this._stepClock = 0;
    this._stepAlt = false;
    this._minePhase = 'alive';
    this._drone = null;

    // Passed to applyPoses every frame; built once so the render loop does not
    // allocate a closure per frame.
    this._nameFor = (id) => roster.get(id)?.displayName ?? 'Player';
  }

  async boot() {
    setBootStatus('starting renderer…');
    const { scene, camera, renderer } = await createScene();
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;

    this.cameraController = new CameraController(camera, renderer);
    this.cameraController.initDesktopEvents();

    this.arena = new ArenaRenderer(scene);
    this.zone = new ZoneRenderer(scene);
    initRemotePlayers(scene);
    initAbilityVFX(scene);
    initTelegraphs(scene, 0);
    initAimReticle(scene);
    initHitFeedback(camera, this.cameraController);

    this.hud = new CombatHUD();
    this.killFeed = new KillFeed('');
    this.deathCam = new DeathCam(this.cameraController);
    this.announcements = new Announcements();

    match.potWei = readPotWei();
    this.hud.setPot(match.potWei);
    this.deathCam.setPot(match.potWei);

    setupKeyboardInput({ onFirstInput: unlockAudio });
    setupMobileControls({
      rendererDomElement: renderer.domElement,
      cameraController: this.cameraController,
    });

    this._wireNetwork();

    setBootStatus('joining match…');
    try {
      await this.connection.join();
    } catch (err) {
      setBootStatus(String(err.message || err), true);
      throw err;
    }

    this.lastFrameTime = performance.now();
    renderer.setAnimationLoop(() => this.tickFrame());
  }

  // ── Network ─────────────────────────────────────────────────

  _wireNetwork() {
    this.connection
      .on('world_init', (msg) => this._onWorldInit(msg))
      .on('snapshot', (msg) => this._onSnapshot(msg))
      .on('events', (events) => this._onEvents(events))
      .on('state', (state) => this._onState(state))
      .on('result', (msg) => this._onResult(msg))
      .on('leave', (code) => this._onLeave(code))
      .on('character_changed', (msg) => this._onCharacterChanged(msg))
      .on('error', ({ message }) => setBootStatus(`server error: ${message}`, true));
  }

  /**
   * The scrim swapped this player's character underneath them.
   *
   * Only a practice room sends this — in a wagered match the character is on
   * the paid entry row and cannot change. Rebuilds the one thing `world_init`
   * built from it: the body, its health, the HUD and the ability bar.
   */
  _onCharacterChanged(msg) {
    const id = msg?.characterId;
    if (!id || !CHARACTERS[id] || id === session.characterId) return;
    session.characterId = id;

    // Dispose before replacing: `_onWorldInit` adds the body once per match, so
    // nothing else in the client is expecting a second one to appear.
    const old = localPlayer.mesh;
    if (old) {
      old.parent?.remove(old);
      old.traverse?.((child) => {
        if (!child.isMesh && !child.isSprite) return;
        child.material?.map?.dispose?.();
        child.material?.dispose?.();
      });
    }
    localPlayer.mesh = createCharacterModel(id, session.userId);
    localPlayer.maxHealth = CHARACTERS[id].maxHealth;
    localPlayer.health = localPlayer.maxHealth;
    this.scene.add(localPlayer.mesh);
    this.hud.setCharacter(id);
  }

  _onWorldInit(msg) {
    session.matchId = msg.matchId;
    session.mapId = msg.mapId;
    session.userId = msg.you.userId;
    session.characterId = msg.you.characterId;
    session.seat = msg.you.seat;
    session.tickHz = msg.tickHz;
    session.totalTicks = msg.totalTicks;
    session.arenaRadius = msg.arenaRadius;
    session.seedCommitment = msg.seedCommitment;
    session.connected = true;

    const map = getMapDef(msg.mapId, { arenaRadius: msg.arenaRadius });
    // The camera's floor clamp needs to know where the ground is. A map with no
    // floor at all ("none") keeps 0, which is where its platforms sit anyway.
    this._groundY = map.floor.kind === 'none' ? 0 : map.floor.y;
    setCameraGroundLevel(this._groundY);
    if (!mapKnown(msg.mapId)) {
      // Prediction runs against this geometry. If it is not the server's, every
      // ledge mispredicts — worth one loud line rather than a silent rubber-band.
      console.warn(
        `[map] "${msg.mapId}" has no shared definition yet; predicting against the graybox bowl`
      );
    }
    this.map = map;
    this.arena.build(map);
    this.zone.setFloorY(map.floor.kind === 'none' ? 0 : map.floor.y);
    setTelegraphFloor(map.floor.kind === 'none' ? 0 : map.floor.y);

    this.predictor = new Predictor({
      playerId: session.userId,
      characterId: session.characterId,
      map,
      seed: msg.seedCommitment || 'client',
    });
    this.reconciler = new Reconciler(this.predictor, this.sender);

    const spawn = (msg.spawns ?? []).find((s) => s.seat === msg.you.seat);
    if (spawn) this.predictor.setSpawn(spawn.x, spawn.y, spawn.z, spawn.yaw ?? 0);

    // The local body. Built from the character on the PAID ENTRY ROW, which the
    // server put in `world_init` — the client never picked it. The seed is the
    // stable user id, so this body is YOUR body, tonight and every night.
    localPlayer.mesh = createCharacterModel(session.characterId, session.userId);
    localPlayer.maxHealth = (CHARACTERS[session.characterId] ?? CHARACTERS.volt).maxHealth;
    localPlayer.health = localPlayer.maxHealth;
    this.scene.add(localPlayer.mesh);

    this.hud.setCharacter(session.characterId);
    this.killFeed.setLocalUserId(session.userId);

    const meta = getMapMeta(msg.mapId);
    setBootStatus(`${meta.name} — waiting for the drop`);
    this.ready = true;
    this.connection.sendReady();
    setTimeout(() => boot.el.classList.add('hidden'), 400);

    // Fire-and-forget: build and compile every lazily-created VFX program now,
    // behind the boot overlay, instead of on the first mid-fight cast — on the
    // WebGL2 fallback each first cast was a 23-78ms synchronous GLSL compile.
    prewarmShaders(this.renderer, this.scene, this.camera);
  }

  _onSnapshot(snapshot) {
    if (!this.predictor) return;
    this.snapshots.push(snapshot);

    match.tick = snapshot.tick;
    match.aliveCount = snapshot.aliveCount;
    match.timeRemainingMs = snapshot.timeRemainingMs;
    match.zone = snapshot.zone;

    // The server's clock wins. Ours only fills the gaps between snapshots.
    this.tick = snapshot.tick;

    const mine = snapshot.players.find((p) => p.id === session.userId);
    if (!mine) return;

    this.reconciler.apply(snapshot, mine);

    localPlayer.health = mine.health;
    localPlayer.ultCharge = mine.ultCharge;
    localPlayer.phase = mine.phase;
    localPlayer.cooldowns = mine.cooldowns;
    localPlayer.effects = mine.effects;

    if (this._lastHealth !== null && mine.health < this._lastHealth && !this._hitThisSnapshot) {
      // Damage with no `hit` event behind it: the zone, or a damage-over-time.
      // The zone reports 0.4 HP per snapshot at 6dps, so showing each delta
      // would be a stream of "0"s — it accumulates until it is worth a number.
      this._driftDamage = (this._driftDamage ?? 0) + (this._lastHealth - mine.health);
      if (this._driftDamage >= 4) {
        onTookDamage(this._driftDamage, null, localPlayer.renderPos);
        this._driftDamage = 0;
      }
    }
    this._hitThisSnapshot = false;
    this._lastHealth = mine.health;
    onHealthChanged(mine.health, localPlayer.maxHealth);

    // A wagered match never resurrects, but the gym and god mode do. Bodies
    // burst into existence instead of just existing — and a scattered toy is
    // put back together from the same seed-identical pieces.
    if (this._minePhase === 'dead' && mine.phase === 'alive') {
      localPlayer.mesh?.userData.driver?.revive();
      spawnParticles(_scratchA.set(mine.x, mine.y, mine.z), 0x00d4ff, 20, 4);
      playSpawnSound();
    }
    this._minePhase = mine.phase;

    this.hud.update(mine, snapshot.tick, match);

    if (mine.phase === 'dead' && !this.dead) this._enterDeathCam();
  }

  _onEvents(events) {
    for (const ev of events) {
      switch (ev.t) {
        case 'ability_cast':
          this._onAbilityCast(ev);
          break;
        case 'hit':
          this._onHit(ev);
          break;
        case 'eliminated':
          this._onEliminated(ev);
          break;
        case 'zone_shrink':
          this.announcements.push('zone', zoneLabel(ev.targetRadius), ev.warnMs);
          this._zoneWarnUntil = performance.now() + ev.warnMs;
          break;
        case 'chaos_event':
          this.announcements.push('chaos', ev.label, ev.warnMs);
          screenFlash('#b46cff', 320, 0.2);
          break;
        case 'match_end':
          this._onMatchEnd(ev);
          break;
        default:
          break;
      }
    }
  }

  _onAbilityCast(ev) {
    const row = roster.get(ev.playerId);
    const characterId = row?.characterId ?? session.characterId;
    const ability = abilityById(characterId, ev.abilityId);

    // ONE origin per cast, chosen by the anchor the sim resolves with — VFX and
    // telegraph both receive it, so the three (resolve, decal, effect) can
    // never start from different places. WORLD casts live at the event's own
    // x/z, the frozen origin the sim resolves them from; BODY casts live on the
    // caster's rendered transform, which the sim's live-position resolve maps
    // to on this screen (remote bodies render ~100ms behind — the effect
    // belongs on the body as DRAWN, not on a snapshot value).
    const live = this._positionOf(ev.playerId, _scratchA);
    const worldAnchored = ability ? abilityAnchor(ability) === 'world' : false;
    const origin = live ?? _scratchA.set(ev.x, PLAYER_HALF_HEIGHT, ev.z);
    if (worldAnchored) origin.set(ev.x, origin.y, ev.z);

    playAbilityCast(ev.abilityId, characterId, ev.playerId, origin, ev.yaw);

    // The telegraph is the reactable half of the wind-up. It is drawn for
    // everyone, including yourself — knowing your own commit window is how you
    // learn to stop cancelling into it.
    showTelegraph({
      abilityId: ev.abilityId,
      characterId,
      playerId: ev.playerId,
      x: origin.x,
      y: origin.y - PLAYER_HALF_HEIGHT,
      z: origin.z,
      yaw: ev.yaw,
    });

    // Cast audio is the main warning for something being wound up behind you,
    // but eight simultaneous casters would be mush. Anything past ~28 units is
    // not a threat to you yet and stays silent.
    if (ability) {
      const mine = ev.playerId === session.userId;
      if (mine || origin.distanceTo(localPlayer.renderPos) < 28) {
        playCastSound(ability.windupMs, mine ? 300 : 220);
      }
    }

    const mesh = ev.playerId === session.userId ? localPlayer.mesh : remotePlayers.get(ev.playerId);
    if (mesh && ability) {
      applyCastAnticipation(mesh, ability.windupMs);
      // The cast layer animates the ability's SHAPE (`hit.kind` picks the
      // archetype) on the ability's OWN clock: the chamber stretches to the
      // real wind-up, so the coil on the body IS the reactable telegraph, and
      // the strike lands when the sim resolves it.
      mesh.userData.driver?.cast(performance.now(), ability);
    }

    if (ev.abilityId === 'dash') {
      playDashSound();
      // The dash pose: the whole body commits into the travel for the sim's
      // exact i-frame window — on every body, yours and theirs.
      mesh?.userData.driver?.dash(performance.now(), DASH_DURATION_MS);
      // The FOV punch is the whole reason a dash reads as fast. Only your own.
      if (ev.playerId === session.userId) this.cameraController.punchFov(6);
    }
  }

  _onHit(ev) {
    const point = _scratchA.set(ev.x, ev.y, ev.z);
    const attacker = roster.get(ev.attackerId);
    const target = roster.get(ev.targetId);
    const heavy = ev.damage >= 25;

    playHitVFX(ev.abilityId, attacker?.characterId ?? 'volt', point, heavy);
    abilityVFX.flashHitTarget?.(ev.targetId);
    // The flinch pose pairs with the white-out flash above — spine arched,
    // arms flung wide, on whoever took the hit (yourself included).
    const flinched = ev.targetId === session.userId ? localPlayer.mesh : remotePlayers.get(ev.targetId);
    flinched?.userData.driver?.hit(performance.now());

    // Chain Lightning resolves as one `hit` per link, and the arc between the
    // caster and each victim is the only thing that makes the chain legible —
    // three separate impact puffs read as three unrelated hits.
    if (ev.abilityId === 'chain_lightning') {
      const source = this._positionOf(ev.attackerId, _scratchB);
      if (source) playChainArc(source, point, attacker?.characterId ?? 'volt');
    }

    if (ev.attackerId === session.userId) {
      onDealtDamage(point, ev.damage, target?.maxHealth ?? 100);
    }
    if (ev.targetId === session.userId) {
      this._hitThisSnapshot = true;
      const from = this._positionOf(ev.attackerId, _scratchB);
      onTookDamage(ev.damage, from, localPlayer.renderPos);
    }
  }

  _onEliminated(ev) {
    this.killFeed.onElimination(ev, (id) => {
      const row = roster.get(id);
      return row ? { name: row.displayName, characterId: row.characterId } : null;
    });

    const at = this._positionOf(ev.victimId, _scratchA);
    if (at) playEliminationVFX(at, roster.get(ev.victimId)?.characterId ?? 'volt');

    if (ev.killerId === session.userId) onKillConfirmed();
    if (ev.victimId === session.userId) {
      this._pendingDeath = {
        killerId: ev.killerId,
        killerName: roster.get(ev.killerId)?.displayName ?? null,
        placement: ev.placement,
      };
    }
  }

  _onMatchEnd(ev) {
    match.ended = true;
    match.winnerId = ev.winnerId;
    match.endReason = ev.reason;
    const winner = ev.winnerId ? roster.get(ev.winnerId) : null;
    this.deathCam.showResult({
      winnerName: winner?.displayName ?? null,
      isWinner: ev.winnerId === session.userId,
    });
    resetHitFeedback();
    this._drone?.stop();
    this._drone = null;

    if (ev.winnerId === session.userId) {
      playWinFanfare();
      screenFlash('#f1c40f', 600);
      spawnParticles(localPlayer.renderPos, 0xf1c40f, 40, 10);
    }
  }

  /** Colyseus Schema — the coarse channel. Never positional. */
  _onState(state) {
    if (state.phase !== session.phase) this._onPhaseChange(state.phase);
    session.phase = state.phase;
    state.players.forEach((p, userId) => {
      roster.set(userId, {
        userId,
        displayName: p.displayName || userId.slice(0, 8),
        characterId: p.characterId,
        // Carried through because it is the only stable ORDER a roster has —
        // a Map iterates in insertion order, which is join order, so a list
        // sorted by anything else jumps around as people come and go.
        seat: p.seat,
        health: p.health,
        maxHealth: p.maxHealth,
        phase: p.phase,
        placement: p.placement,
        kills: p.kills,
        damageDealt: p.damageDealt,
        connected: p.connected,
      });
    });
  }

  /**
   * The room only accepts `input` during combat and only ships snapshots then.
   *
   * Sampling before that would queue up to twenty seconds of frames the server
   * will never ack, and the first snapshot would then replay the whole backlog
   * through the sim in one frame. So the input pipeline starts empty, exactly
   * when the server starts listening.
   */
  _onPhaseChange(phase) {
    if (phase !== 'combat') return;
    this.sender.reset();
    this.reconciler?.reset();
    this.accumulatorMs = 0;
    setBootStatus('');
    boot.el.classList.add('hidden');
    this._spawnMoment();
    this._playCountdown();
  }

  /** Every body bursts into the match instead of just being there. */
  _spawnMoment() {
    if (this.predictor) {
      const p = this.predictor.player;
      spawnParticles(_scratchA.set(p.x, p.y, p.z), 0x00d4ff, 20, 4);
    }
    for (const [, mesh] of remotePlayers) spawnParticles(mesh.position, 0x00d4ff, 20, 4);
    playSpawnSound();
  }

  /**
   * 3-2-1-GO. Presentation only — input is already live underneath it, so a
   * player who moves on "2" is not punished, just early.
   */
  _playCountdown() {
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        this.announcements.countdown(String(3 - i));
        playCountdownBeep(440);
      }, i * 1000);
    }
    setTimeout(() => {
      this.announcements.countdown('GO!', { gold: true });
      playCountdownBeep(880);
    }, 3000);
  }

  _onResult(msg) {
    match.potWei = msg.potWei ?? match.potWei;
    this.hud.setPot(match.potWei);
    this.deathCam.setPot(msg.payoutWei ?? match.potWei);

    // Tell the shell to move to the results screen. A NAVIGATION HINT ONLY —
    // apps/web re-fetches the result server-side, so this cannot invent a
    // placement or a payout. Sent from `result` rather than from `match_end`
    // because settlement has run by the time `result` arrives.
    postMatchEnded(msg.matchId ?? session.matchId);
  }

  _onLeave(code) {
    session.connected = false;
    releaseAll();
    if (!match.ended) setBootStatus(`disconnected (${code})`, true);
  }

  // ── Death ───────────────────────────────────────────────────

  _enterDeathCam() {
    this.dead = true;
    releaseAll();

    // The v1 death moment (PhysicsEngine.js:70-76): audible, red, shaken — then
    // a beat on your own corpse before the death cam takes the screen. The body
    // stays visible through the hold so the elimination VFX has something to
    // happen TO.
    playDeathSound();
    screenFlash('#e74c3c', 400);
    triggerCameraShake(0.5, 300);
    postFX.setDeathGrade?.(1);
    // The toy falls apart — the scatter plays through the hold below, so your
    // own defeat is the same fall-apart everyone else watches.
    localPlayer.mesh?.userData.driver?.die();

    // A death you come back from is not the death this screen was built for.
    //
    // `DeathCam` is the end of somebody's match — killer view, free camera, live
    // pot, requeue. In a room that respawns you it opened 700ms after you died
    // and was torn down 1.8s later by the respawn, so a practice death flashed a
    // full match-over screen and then took it away. Asked directly: "what
    // happens when i die?" — that flicker is what happened.
    //
    // The connection knows which kind of room it is; nothing else does.
    // `Connection.willRespawn` returns false, so an arena match falls through to
    // the death screen and a gym or scrim overrides it.
    if (this.connection.willRespawn()) {
      this.announcements.push('zone', 'ELIMINATED — respawning');
      if (localPlayer.mesh) localPlayer.mesh.visible = false;
      return;
    }

    setTimeout(() => {
      // Revived during the hold (god mode) or the match ended and showResult
      // already owns the screen.
      if (!this.dead || match.ended) return;
      // Read here, not at death: the `eliminated` event that fills _pendingDeath
      // arrives in the same batch as — but after — the snapshot that killed us.
      const info = this._pendingDeath ?? { killerId: null, killerName: null, placement: 0 };
      const me = roster.get(session.userId);
      this.deathCam.show({
        ...info,
        kills: me?.kills ?? 0,
        damageDealt: me?.damageDealt ?? 0,
      });
      if (localPlayer.mesh) localPlayer.mesh.visible = false;
    }, 700);
  }

  // ── Frame ───────────────────────────────────────────────────

  tickFrame() {
    const now = performance.now();
    let delta = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;
    // A backgrounded tab returns with a multi-second delta. Clamping keeps one
    // stall from becoming a hundred catch-up ticks in a single frame.
    if (delta > 0.25) delta = 0.25;
    frame.delta = delta;
    frame.now = now;

    const playing = this.ready && !this.dead && !match.ended && session.phase === 'combat';
    if (playing) this._stepSimulation(now, delta);

    this._render(now, delta);
    renderFrame();
  }

  /**
   * The fixed-timestep half.
   *
   * Note that hitstop does NOT gate this. Freezing input sampling for 80ms would
   * send the server a gap it fills with coast frames — you would stop moving
   * every time you landed a hit.
   */
  _stepSimulation(now, delta) {
    this.accumulatorMs += delta * 1000;

    let steps = 0;
    while (this.accumulatorMs >= TICK_MS && steps < MAX_CATCHUP_TICKS) {
      this.tick += 1;
      const intent = readIntent();
      const actions = consumeActions();
      const frameOut = this.sender.sample(this.tick, this.cameraController.getYaw(), intent, actions);
      if (frameOut) {
        this.predictor.step(frameOut);
        this.reconciler.record(frameOut);
      }
      this.accumulatorMs -= TICK_MS;
      steps += 1;
    }
    // Whatever is left after the catch-up cap is dropped rather than carried:
    // carrying it means the next frame runs the cap again and never recovers.
    if (this.accumulatorMs >= TICK_MS) this.accumulatorMs = 0;

    if (now - this.lastFlush >= INPUT_FLUSH_MS) {
      this.lastFlush = now;
      this.sender.flush();
    }
  }

  _render(now, delta) {
    const hitstopped = isHitstopped(now);
    // Presentation freezes during hitstop; everything above this line does not.
    const visualDelta = hitstopped ? 0 : delta;

    if (this.predictor) {
      const alpha = Math.min(1, this.accumulatorMs / TICK_MS);
      this.predictor.interpolate(alpha, localPlayer.renderPos);
      localPlayer.grounded = this.predictor.player.grounded;
      localPlayer.velocityY = this.predictor.player.vy;

      const mesh = localPlayer.mesh;
      if (mesh && !this.dead) {
        mesh.position.copy(localPlayer.renderPos);
        // Models are authored facing +Z; forward is (-sin yaw, -cos yaw), hence +PI.
        // Damped, not assigned: the sim updates yaw at 30Hz while the camera
        // turns every frame, so a raw assignment ratchets the body in visible
        // 33ms steps under any camera movement. 20/s is v1's follow feel
        // (PhysicsEngine.js:351 used 15/s), slightly tighter because this yaw
        // is also the aim.
        const targetYaw = this.predictor.player.yaw + Math.PI;
        mesh.rotation.y += shortAngleDist(mesh.rotation.y, targetYaw) * Math.min(1, visualDelta * 20);
        updateSquashStretch(mesh, localPlayer.velocityY);
        // The pose driver owns the joints: run cycle from the PREDICTOR's own
        // velocity (the same numbers the body moves by), aim from the damped
        // visual yaw — legs face the travel, torso faces the aim.
        mesh.userData.driver?.update(now, visualDelta, {
          vx: this.predictor.player.vx,
          vz: this.predictor.player.vz,
          aimYaw: mesh.rotation.y,
        });
        updateDashTrail(mesh, now);
        updateCastPose(mesh, now);
        setCharacterShimmer(mesh, localPlayer.effects.includes('invisible') ? 0.45 : 0);
        // The same call the remote path makes, so your own buffs and debuffs
        // read exactly the way your opponents' do. A status you can only see on
        // other people is half a feature. Tinted by the body's own family
        // colour so the aura and the body it wraps agree.
        setStatusAura(mesh, localPlayer.effects, mesh.userData.colorHex);
        // YOUR health, over YOUR head — the same bar everyone else wears.
        //
        // It was only on the corner HUD, which means the one number you check
        // most often lived at the far edge of the screen while your eyes were
        // on your character in the middle of it. Brawl Stars puts it over the
        // body for that reason, and it makes the fight symmetric: the bar you
        // read on an opponent and the bar you read on yourself are the same
        // object with the same colours at the same size. The HUD copy stays —
        // it carries the exact numbers and the ult meter.
        attachHealthBar(mesh, session.characterId);
        updateHealthBar(mesh, localPlayer.health, this.camera.position);
        updateLeaderCrown(mesh, match.aliveCount === 2 && localPlayer.phase === 'alive');
      } else if (mesh && this.dead && mesh.userData.driver?.dead) {
        // The body is out of the fight but the toy is still falling apart —
        // the scatter physics runs through the death-cam hold.
        mesh.userData.driver.update(now, visualDelta, {});
      }

      this._updateMovementJuice(delta);
    }

    // Remote players are drawn 100ms in the past, interpolated between two
    // received snapshots. Never extrapolated — see net/snapshotBuffer.js.
    const poses = this.snapshots.sample(now);
    applyPoses(poses, Math.max(visualDelta, 1e-4), now, this._nameFor);
    for (const [id, mesh] of remotePlayers) {
      const pose = poses.get(id);
      if (!pose) continue;
      attachHealthBar(mesh, pose.characterId);
      updateHealthBar(mesh, pose.health, this.camera.position);
      updateLeaderCrown(mesh, match.aliveCount === 2 && pose.phase === 'alive');
    }

    this.cameraController.updateFreeMovement(delta, keys);
    this.cameraController.update(delta);
    // After the camera, because the reticle is drawn where the camera's aim ray
    // landed this frame.
    updateAimReticle(this.cameraController.aimPoint, getAimSlot(), this._groundY ?? 0);
    this.zone.update(match.zone, now < (this._zoneWarnUntil ?? 0));

    updateAbilityVFX();
    updateTelegraphs();
    updateParticles(visualDelta);
    updateHitFeedback();
    updateEnvironmentEffects(visualDelta, this.camera.position);
    updateConveyorScrolls(visualDelta);

    this.killFeed.update();
    this.announcements.update();
    this.deathCam.update(now);
    this._updateTension();

    if (isDebug) this._updateDebug();
  }

  /**
   * Jump chirp, landing impact, footsteps — edge-detected off the predictor
   * every render frame. v1 played all three from its physics loop; the sim is
   * shared now, so the edges are read here instead. Purely presentational.
   */
  _updateMovementJuice(delta) {
    const p = this.predictor.player;
    const playing = !this.dead && !match.ended && session.phase === 'combat';

    if (playing) {
      // grounded -> airborne with upward velocity: a jump. Walking off a ledge
      // leaves the ground falling and stays silent, exactly like v1.
      if (this._wasGrounded && !p.grounded && p.vy > 1) playJumpSound();

      // airborne -> grounded: the impact scales with the fastest fall speed of
      // the airtime, because the sim may have zeroed vy by the grounded frame.
      if (!this._wasGrounded && p.grounded) {
        const impact = -this._peakFallVy;
        if (impact > 12) {
          _scratchA.copy(localPlayer.renderPos);
          _scratchA.y -= 1; // feet, not chest
          spawnParticles(_scratchA, 0xcccccc, 10, 3, { upBias: 0.3 });
          playLandSound(Math.min(1, impact / 40));
          triggerCameraShake(Math.min(0.25, 0.1 + (impact - 12) * 0.005), 90);
        }
      }

      const hSpeed = Math.hypot(p.vx, p.vz);
      if (p.grounded && hSpeed > 2) {
        this._stepClock += delta;
        const stride = Math.min(0.5, Math.max(0.18, 5.2 / hSpeed));
        if (this._stepClock >= stride) {
          this._stepClock = 0;
          this._stepAlt = !this._stepAlt;
          playFootstep(this._stepAlt);
        }
      } else {
        // Primed so the first footfall lands just after movement starts.
        this._stepClock = 0.12;
      }
    }

    if (!p.grounded) this._peakFallVy = Math.min(this._peakFallVy, p.vy);
    else this._peakFallVy = 0;
    this._wasGrounded = p.grounded;
  }

  /**
   * Endgame tension bed: a low drone once three or fewer are standing, or the
   * zone is on its final contraction, pulsing faster as the circle closes.
   */
  _updateTension() {
    const zone = match.zone;
    const finalZone = zone.dps > 0 && zone.targetRadius <= 0.01;
    const active =
      session.connected &&
      session.phase === 'combat' &&
      !match.ended &&
      match.aliveCount >= 2 &&
      (match.aliveCount <= 3 || finalZone);

    if (!active) {
      this._drone?.stop();
      this._drone = null;
      return;
    }

    if (!this._drone) this._drone = startTensionDrone();
    let t = match.aliveCount <= 2 ? 0.6 : 0.35;
    if (finalZone) {
      const closure = 1 - zone.radius / Math.max(1, session.arenaRadius);
      t = Math.max(t, Math.min(1, 0.4 + closure * 0.6));
    }
    this._drone.setIntensity(t);
  }

  _positionOf(id, out) {
    if (!id) return null;
    if (id === session.userId) return out.copy(localPlayer.renderPos);
    const mesh = remotePlayers.get(id);
    return mesh ? out.copy(mesh.position) : null;
  }

  _updateDebug() {
    if (!this._debugEl) {
      this._debugEl = document.createElement('div');
      this._debugEl.style.cssText =
        'position:fixed;left:8px;top:8px;z-index:50;font:11px/1.5 ui-monospace,monospace;background:rgba(0,0,0,.6);padding:6px 8px;border-radius:6px;pointer-events:none;white-space:pre';
      document.body.appendChild(this._debugEl);
    }
    const s = this.reconciler?.stats ?? {};
    this._debugEl.textContent = [
      `tick ${this.tick} (srv ${match.tick})`,
      `rtt ${Math.round(this.connection.rttMs)}ms`,
      `snap age ${Math.round(this.snapshots.ageMs())}ms`,
      `err ${(s.lastError ?? 0).toFixed(3)}  corrections ${s.corrections ?? 0}`,
      `unacked ${this.sender.unacked().length}`,
      `remotes ${remotePlayers.size}`,
    ].join('\n');
  }
}

function abilityById(characterId, abilityId) {
  const def = CHARACTERS[characterId];
  if (!def) return null;
  for (const slot of ['basic', 'skill1', 'skill2', 'ultimate']) {
    if (def.abilities[slot].id === abilityId) return def.abilities[slot];
  }
  return null;
}

export function startGame(connection) {
  const game = new Game(connection);
  game.boot().catch((err) => {
    console.error('[boot]', err);
  });

  // Handy in the console; deliberately read-only in spirit — there is nothing
  // here that could change an outcome even if it were written.
  if (isDebug) window.chaos = { game, session, match, roster, isTouch };
  return game;
}

// The gym boots the same Game itself, against a LocalConnection, and sets this
// first — otherwise importing this module would also start a second game
// against the real server.
if (!window.__chaosGymBoot) startGame();
